import { useCallback, useEffect, useState } from "react";
import { Copy, Download, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Global toolbar that copies or downloads the ACTIVE tab's visible content.
 * Reads the currently-visible <div role="tabpanel" data-state="active">
 * and extracts its plain-text content — works for every tab in the
 * astrology / homeopathy / scripture / storyteller row with zero per-tab wiring.
 */
export default function ActiveTabExporter() {
  const [copied, setCopied] = useState(false);
  const [tabName, setTabName] = useState("Reading");
  const [visible, setVisible] = useState(false);

  // Track which tab is active so the toolbar knows what to name the download
  useEffect(() => {
    const update = () => {
      // The Tabs primitive uses [role="tabpanel"][data-state="active"] for the active panel
      const panel = document.querySelector('[role="tabpanel"][data-state="active"]');
      if (!panel) { setVisible(false); return; }
      // Find the corresponding tab trigger to grab its label
      const val = panel.getAttribute("data-radix-tabs-content-value")
                    || panel.id?.split("-").slice(-1)[0]
                    || "";
      let name = val;
      // Search a trigger with matching value → prefer visible text
      const trigger = document.querySelector(`[role="tab"][data-state="active"]`);
      if (trigger?.innerText) name = trigger.innerText.trim();
      setTabName(name || "Reading");
      setVisible(true);
    };
    update();
    // MutationObserver: re-check when the tab changes
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, subtree: true, attributeFilter: ["data-state"] });
    return () => obs.disconnect();
  }, []);

  const _extractText = useCallback(() => {
    const panel = document.querySelector('[role="tabpanel"][data-state="active"]');
    if (!panel) return "";
    // Preserve headings + paragraph structure. innerText is closer to on-screen text than textContent.
    return panel.innerText || panel.textContent || "";
  }, []);

  const copy = useCallback(async () => {
    const txt = _extractText();
    if (!txt) { toast.error("Nothing to copy"); return; }
    // Modern Clipboard API (secure contexts)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(txt);
        setCopied(true); toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1500); return;
      } catch { /* fall through to legacy fallback */ }
    }
    // Legacy fallback (older browsers / non-secure)
    try {
      const ta = document.createElement("textarea");
      ta.value = txt; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true); toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed — please select text manually"); }
  }, [_extractText]);

  const download = useCallback(() => {
    const txt = _extractText();
    if (!txt) { toast.error("Nothing to download"); return; }
    const safe = (tabName || "reading").replace(/[^\w\-]+/g, "_").toLowerCase();
    const now = new Date().toISOString().slice(0, 10);
    const blob = new Blob([`${tabName} — ${now}\n${"=".repeat(60)}\n\n${txt}`],
                            { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${safe}-${now}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success("Section downloaded");
  }, [_extractText, tabName]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#0F1115]/95 backdrop-blur
                     border border-[#D4AF37]/40 rounded-sm px-2 py-1.5 shadow-2xl"
         data-testid="active-tab-exporter">
      <span className="text-[9px] uppercase tracking-widest font-mono text-[#A8896C] pl-1">
        Section
      </span>
      <button onClick={copy} data-testid="tab-copy-btn"
              className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono
                         text-[#D4AF37] border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10
                         px-2.5 py-1.5 rounded-sm transition-colors">
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "Copied" : "Copy"}
      </button>
      <button onClick={download} data-testid="tab-download-btn"
              className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono
                         text-[#D4AF37] border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10
                         px-2.5 py-1.5 rounded-sm transition-colors">
        <Download size={12} /> Download
      </button>
    </div>
  );
}
